import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, Workflow, BookOpen } from 'lucide-react';
import { BlurFade } from '../magicui/blur-fade';
import { BorderBeam } from '../magicui/border-beam';
import ComingSoonBadge from '../ComingSoonBadge';
import { pdfFlowsUrl } from '../../data/site';

const facts = ['Windows 10 & 11', 'Works offline', 'No account', 'Free to use'];

export default function Products() {
  return (
    <section id="products" className="bg-white py-24 lg:py-32">
      <div className="container-x">
        <div className="max-w-2xl">
          <BlurFade inView>
            <p className="eyebrow">Our products</p>
          </BlurFade>
          <BlurFade inView delay={0.1}>
            <h2 className="h-section mt-5">Tools that quietly do the heavy lifting.</h2>
          </BlurFade>
        </div>

        <div className="mt-14 grid gap-5 lg:grid-cols-[1.6fr_1fr]">
          <BlurFade inView delay={0.1} className="h-full">
            <article className="relative flex h-full flex-col overflow-hidden rounded-[2rem] bg-ink p-8 text-white sm:p-10">
              <div aria-hidden="true" className="absolute -right-24 -top-24 size-80 rounded-full bg-fusion-blue/30 blur-3xl" />
              <div aria-hidden="true" className="absolute -bottom-32 left-10 size-72 rounded-full bg-fusion-teal/20 blur-3xl" />
              <div className="relative flex items-center gap-3">
                <span className="inline-flex size-12 items-center justify-center rounded-2xl bg-white/10 ring-1 ring-white/15">
                  <Workflow className="size-6" aria-hidden="true" />
                </span>
                <div>
                  <p className="font-display text-2xl font-semibold tracking-[-0.02em]">PDF Flows</p>
                  <p className="font-mono text-xs uppercase tracking-[0.16em] text-white/55">PDF workflow automation</p>
                </div>
              </div>
              <p className="relative mt-8 max-w-xl text-lg leading-relaxed text-white/80">
                Chain OCR, conversion, watermarking, splitting, merging and encryption into one-click workflows
                that run entirely on your Windows PC. Set the course once — every document after that arrives
                finished.
              </p>
              <ul className="relative mt-8 flex flex-wrap gap-2">
                {facts.map((f) => (
                  <li key={f} className="rounded-full bg-white/10 px-3.5 py-1.5 text-xs font-medium text-white/85 ring-1 ring-white/10">
                    {f}
                  </li>
                ))}
              </ul>
              <div className="relative mt-auto flex flex-wrap gap-3 pt-10">
                <Link to="/pdf-flows" className="inline-flex min-h-11 items-center gap-2 rounded-full bg-white px-6 text-sm font-semibold text-ink transition hover:bg-white/90">
                  Learn more <ArrowRight className="size-4" aria-hidden="true" />
                </Link>
                <a href={pdfFlowsUrl} target="_blank" rel="noopener noreferrer" className="inline-flex min-h-11 items-center gap-2 rounded-full px-6 text-sm font-semibold text-white ring-1 ring-white/25 transition hover:bg-white/10">
                  Visit pdf-flows.com <ArrowUpRight className="size-4" aria-hidden="true" />
                </a>
              </div>
              <BorderBeam size={180} duration={10} colorFrom="#3355FF" colorTo="#14B8A6" borderWidth={1.5} />
            </article>
          </BlurFade>

          <BlurFade inView delay={0.2} className="h-full">
            <article className="relative flex h-full flex-col overflow-hidden rounded-[2rem] border border-line bg-mist p-8 sm:p-10">
              <div aria-hidden="true" className="absolute -right-16 -top-16 size-56 rounded-full bg-fusion-ember/15 blur-3xl" />
              <div className="relative flex items-start justify-between gap-4">
                <span className="inline-flex size-12 items-center justify-center rounded-2xl bg-white text-[#C2410C] shadow-card">
                  <BookOpen className="size-6" aria-hidden="true" />
                </span>
                <ComingSoonBadge />
              </div>
              <p className="relative mt-8 font-display text-2xl font-semibold tracking-[-0.02em]">InstruKit</p>
              <p className="relative font-mono text-xs uppercase tracking-[0.16em] text-ink-3">Offline field-manual library</p>
              <p className="relative mt-4 leading-relaxed text-ink-3">
                Manuals, schematics and service notes for the people who fix things — searchable in seconds and
                available offline. Launching soon for Windows.
              </p>
              <Link to="/instrukit" className="relative mt-auto inline-flex items-center gap-2 pt-8 text-sm font-semibold text-ink">
                <span className="link-underline">Learn more</span> <ArrowRight className="size-4" aria-hidden="true" />
              </Link>
            </article>
          </BlurFade>
        </div>
      </div>
    </section>
  );
}
